import React, { memo, useState } from 'react';
import { Badge } from 'reactstrap';
import { Calendar, EventContentArg } from '@fullcalendar/core';
import { CalendarEventUserId, CalendarEventUserResponse } from '@business/entities/calendar_event';
import { Session } from '@modules/auth/session';
import { UserService } from '@business/services';
import { useObservable } from '@core/utils/hooks/rxjs';

export interface ListViewProps {
  event: EventContentArg;
  updateEventUserTask: (eventUserId: CalendarEventUserId, isAdmin: boolean) => void;
}

export const ListView = memo((props: ListViewProps) => {
  const { event, updateEventUserTask } = props;

  if (!event.view.type.startsWith('list')) {
    return true;
  }

  const item = event.event.extendedProps;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 3fr', alignItems: 'center' }}>
      <div style={{ fontWeight: 'bold', paddingRight: '10px' }}>{event.event.title}</div>
      <div>
        {item.users?.length > 0 &&
          event.isEnd &&
          item.users.map((user, index: number) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
              <TaskChecklistItem user={user} updateEventUserTask={updateEventUserTask} />
            </div>
          ))}
      </div>
    </div>
  );
});

export interface TaskChecklistProps {
  user: CalendarEventUserResponse;
  updateEventUserTask: (eventUserId: CalendarEventUserId, isAdmin: boolean) => void;
}

export const TaskChecklistItem = memo((props: TaskChecklistProps) => {
  const { user, updateEventUserTask } = props;

  const sessionUser = useObservable(Session.user$);

  const [isChecked, setIsChecked] = useState(Boolean(user.isChecked));

  return (
    <div className="custom-control custom-checkbox d-flex" style={{ alignItems: 'center', marginBottom: 0, paddingBottom: 0, minHeight: 'unset' }}>
      <input
        type="checkbox"
        disabled={isChecked || user.id !== sessionUser?.id}
        checked={isChecked}
        onChange={e => {
          e.preventDefault();
          e.stopPropagation();
          setIsChecked(true);
          updateEventUserTask(user.eventUserId, false);
        }}
        id={`checkbox-${user.eventUserId}`}
        style={{
          width: '20px',
          height: '20px',
          borderRadius: '5px',
          cursor: isChecked || user.id !== sessionUser?.id ? 'not-allowed' : 'pointer',
          transition: 'background-color 0.2s, border 0.2s',
          outline: 'none',
          position: 'relative',
        }}
      />
      {!!(user.isChecked && !user.isApproved && UserService.isAuthUserStaff()) && (
        <Badge
          color="primary"
          style={{ marginLeft: '8px', cursor: 'pointer', alignSelf: 'center' }}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            updateEventUserTask(user.eventUserId, true);
          }}
        >
          Approve
        </Badge>
      )}
      {!!(isChecked && user.isApproved) && (
        <Badge color="success" style={{ marginLeft: '8px', alignSelf: 'center' }}>
          Approved
        </Badge>
      )}
      {!!(isChecked && !user.isApproved && !UserService.isAuthUserStaff()) && (
        <Badge color="warning" style={{ marginLeft: '8px', alignSelf: 'center' }}>
          Pending Approval
        </Badge>
      )}

      <label
        htmlFor={`checkbox-${user.eventUserId}`}
        style={{
          fontSize: '14px',
          color: isChecked ? 'gray' : 'black',
          marginLeft: '10px',
          cursor: isChecked || user.id !== sessionUser.id ? 'not-allowed' : 'pointer',
        }}
      >
        {user.email}
      </label>
    </div>
  );
});
